// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { getCartLineErrorMessages } from "@hardwoods/App/Components/MiniCart/MiniCartHelper";
import Notify, { NotifyProps } from "@hardwoods/App/Components/Notify/Notify";
import xExpressCheckout from "@hardwoods/App/Handlers/Checkout/ExpressCheckout";
import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import CartLineQuantity, { CartLineQuantityStyles } from "@hardwoods/Overrides/Components/CartLineQuantity";
import ProductPrice, { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import FancyScroll from "@hardwoods/XDL/components/common/FancyScroll";
import Trash from "@hardwoods/XDL/Icons/Trash";
import { fontConfig } from "@hardwoods/XDL/PreStyle/typography";
import { color, ellipsis } from "@hardwoods/XDL/Utilities";
import { isCheckoutDisabledHandler } from "@hardwoods/XDL/Utilities/helper";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { styledScrollbar } from "@hardwoods/XDL/Utilities/SharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    canSubmitRequisition,
    getCurrentCartState,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import GridContainer from "@insite/mobius/GridContainer";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const isCheckoutDisabled = isCheckoutDisabledHandler(cart);
    const currentPage = getCurrentPage(state);

    return {
        isCheckoutDisabled,
        canSubmitRequisition: canSubmitRequisition(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        shouldLoadCart:
            !getCurrentCartState(state).value &&
            !getCurrentCartState(state).isLoading &&
            currentPage.type !== "CartPage",
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
    xExpressCheckout,
};

interface MiniCartStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    noCartLinesNotify?: NotifyProps;
    viewCartLink?: LinkPresentationProps;
    cartLinesWrapper?: InjectableCss;
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    lineDescription?: LinkPresentationProps;
    brandName?: TypographyProps;
    productInfoWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    qtyAndPriceWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    basicProductPriceStyles?: ProductPriceStyles;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    subTotalWrapper?: InjectableCss;
    subTotalTitle?: TypographyProps;
    subTotalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
}

const miniCartStyles: MiniCartStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 445px;
            max-width: 445px;
            min-height: 200px;
            ${xScreen.lte("sm")} {
                width: 100%;
                max-width: none;
                height: 100vh;
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            padding: 25px 20px 15px;
            border-bottom: 1px solid ${color("common.border")};
            ${xScreen.lte("sm")} {
                padding: 40px 20px 15px;
            }
        `,
    },
    title: {
        size: 16,
        weight: 900,
        fontFamily: fontConfig.headingFontFamily,
        css: css`
            letter-spacing: 0;
            line-height: 20px;
            margin-right: 5px;
        `,
    },
    viewCartLink: {
        color: "secondary",
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    noCartLinesNotify: {
        notifyContainerOverrides: css`
            margin: auto;
            max-width: calc(100% - 20px);
            ${xScreen.lte("sm")} {
                margin: 40px auto 0;
            }
        `,
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            min-height: 150px;
            max-height: 375px;
            padding: 20px;
            overflow: auto;
            & > div:last-child {
                margin-bottom: 10px;
            }
            ${styledScrollbar()}
        `,
    },
    cartLine: {
        css: css`
            display: flex;
            position: relative;
            margin-bottom: 40px;
            ${xScreen.lte("sm")} {
                padding-right: 20px;
            }
            ${xScreen.lte("xs")} {
                flex-direction: column;
                align-items: center;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 72px;
            height: 72px;
            overflow: hidden;
            margin-right: 15px;
            flex-shrink: 0;
            ${xScreen.lte("xs")} {
                margin-bottom: 20px;
            }
        `,
    },
    cartLineImage: {
        width: "72px",
        height: "72px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 282px;
        `,
    },
    productInfoWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-bottom: 10px;
        `,
    },
    partNumbersStyles: {
        container: {
            gap: 5,
            css: css`
                margin-right: 5px;
                width: auto;
                flex: 1 0 auto;
                min-width: 174px;
            `,
        },
        erpNumberLabelText: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                &:after {
                    content: ":";
                    margin-right: 5px;
                }
            `,
        },
        erpNumberValueText: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    productAvailabilityStyles: {
        container: {
            gap: 0,
            css: css`
                width: auto;
                flex: 1 0 auto;
            `,
        },
    },
    lineDescription: {
        typographyProps: {
            size: 14,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                ${ellipsis(2)}
            `,
        },
        css: css`
            margin-bottom: 20px;
        `,
    },
    brandName: {
        size: 12,
        weight: "bold",
        as: "p",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 5px;
        `,
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    cartLineQuantityStyles: {
        quantityWrapper: {
            css: css`
                display: flex;
                align-items: center;
                margin-right: 30px;
            `,
        },

        editableQuantityTextField: {
            sizeVariant: "small",
            cssOverrides: {
                inputSelect: css`
                    width: 55px;
                    text-align: center;
                `,
            },
        },
    },
    extendedPriceWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    basicProductPriceStyles: {
        netPrice: {
            priceText: {
                weight: 900,
                size: 16,
                fontFamily: fontConfig.headingFontFamily,
                css: css`
                    letter-spacing: 0;
                    line-height: 20px;
                `,
            },
            unitOfMeasureText: {
                weight: "normal",
                size: 12,
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                `,
            },
        },
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: Trash,
                size: 12,
                color: "secondary",
                css: css`
                    &&&:hover {
                        color: ${color("secondary.dark")};
                    }
                    margin-right: 0;
                `,
            },
        },
        css: css`
            position: absolute;
            top: 0;
            right: 0;
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div {
                margin-bottom: 10px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            text-align: right;
            border-top: 1px solid ${color("common.border")};
            padding: 18px 15px;
        `,
    },
    subTotalTitle: {
        size: 15,
        css: css`
            margin-right: 15px;
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
    subTotalValue: {
        size: 26,
        weight: 900,
        fontFamily: fontConfig.headingFontFamily,
        css: css`
            letter-spacing: 0;
            line-height: 32px;
        `,
    },
    subTotalWrapper: {
        css: css`
            margin-bottom: 22px;
        `,
    },
    checkoutButton: {
        sizeVariant: "large",
    },
};

const styles = miniCartStyles;
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isShown,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    xExpressCheckout,
    canSubmitRequisition,
    doPunchOutCheckout,
    isUpdatingCartLine,
    isLoading,
}) => {
    const history = useHistory();
    const refFancyScroll = React.useRef<HTMLDivElement>(null);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

    if (!cart) {
        return null;
    }

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const redirectTo = (link?: string) => {
        hideTippy();
        history.push(`${link}`);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        setIsCheckoutLoading(true);

        preloadCheckoutShippingData({
            onSuccess: () => {
                xExpressCheckout({
                    onSuccess: nextUrl => {
                        setIsCheckoutLoading(false);
                        hideAll();
                        history.push(nextUrl);
                    },
                });
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled = isCheckoutDisabled || isCheckoutLoading;
    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoadingCart || isUpdatingCartLine}>
            <StyledWrapper {...styles.wrapper}>
                {!cartLines?.length && (
                    <Notify
                        {...styles.noCartLinesNotify}
                        messageType="info"
                        body={translate("Add some items to cart!")}
                        showCloseButton={false}
                    />
                )}
                {cartLines?.length > 0 && (
                    <>
                        <StyledWrapper {...styles.titleWrapper}>
                            <Typography {...styles.title}>
                                {totalCountDisplay}
                                {translate(" Items Total")}
                            </Typography>
                            <Link
                                {...styles.viewCartLink}
                                onClick={() => redirectTo(cartPageUrl)}
                                data-test-selector="ViewCartLink"
                            >
                                {translate("View Cart")}
                            </Link>
                        </StyledWrapper>
                        <FancyScroll
                            refElement={refFancyScroll}
                            showUp={true}
                            updatedConditions={[isShown, ...(cartLines ?? [])]}
                        >
                            <StyledWrapper
                                ref={refFancyScroll}
                                {...styles.cartLinesWrapper}
                                data-test-selector="cartLines-container"
                            >
                                {cartLines?.map(cartLine => {
                                    const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                    return (
                                        <CartLineContext.Provider value={cartLine} key={`miniCartLine_${cartLine.id}`}>
                                            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                                                <StyledWrapper {...styles.imageWrapper}>
                                                    <Link href={cartLine.productUri} onClick={hideTippy}>
                                                        <LazyImage
                                                            src={cartLine.smallImagePath}
                                                            altText={
                                                                cartLine.altText ? cartLine.altText?.substr(0, 16) : ""
                                                            }
                                                            {...styles.cartLineImage}
                                                        />
                                                    </Link>
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.detailsWrapper}>
                                                    {cartLine.brand && (
                                                        <Typography {...styles.brandName}>
                                                            {cartLine.brand?.name}
                                                        </Typography>
                                                    )}
                                                    <StyledWrapper {...styles.productInfoWrapper}>
                                                        <ProductPartNumbers
                                                            productNumber={cartLine.erpNumber}
                                                            extendedStyles={styles.partNumbersStyles}
                                                        />
                                                        {!cartLine.quoteRequired && (
                                                            <ProductAvailability
                                                                productId={cartLine.productId!}
                                                                availability={cartLine.availability!}
                                                                unitOfMeasure={cartLine.unitOfMeasure}
                                                                trackInventory={cartLine.trackInventory}
                                                                failedToLoadInventory={
                                                                    cart.failedToGetRealTimeInventory
                                                                }
                                                                extendedStyles={styles.productAvailabilityStyles}
                                                            />
                                                        )}
                                                    </StyledWrapper>
                                                    <Link
                                                        href={cartLine.productUri}
                                                        onClick={hideTippy}
                                                        {...styles.lineDescription}
                                                    >
                                                        {cartLine.shortDescription}
                                                    </Link>
                                                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                                                        <CartLineQuantity
                                                            cart={cart as Cart}
                                                            editable={true}
                                                            showUOM={true}
                                                            extendedStyles={styles.cartLineQuantityStyles}
                                                            onQtyOrderedChange={(inputQty: number) => {
                                                                qtyOrderedChangeHandler &&
                                                                    qtyOrderedChangeHandler(cartLine, inputQty);
                                                            }}
                                                        />

                                                        <StyledWrapper {...styles.extendedPriceWrapper}>
                                                            {!cart.cartNotPriced && (
                                                                <>
                                                                    <ProductPrice
                                                                        showPriceUomWithoutDuplicate={true}
                                                                        showLabel={false}
                                                                        product={cartLine}
                                                                    />
                                                                    <ProductPrice
                                                                        showLabel={false}
                                                                        product={cartLine}
                                                                        extendedStyles={styles.basicProductPriceStyles}
                                                                    />
                                                                </>
                                                            )}
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                    {errorMessages && (
                                                        <StyledWrapper {...styles.errorMessagesWrapper}>
                                                            {errorMessages.map(message => (
                                                                <Notify
                                                                    messageType="danger"
                                                                    body={message}
                                                                    data-test-selector="cartline_errorMessage"
                                                                    key={`error_${cartLine.id}`}
                                                                />
                                                            ))}
                                                        </StyledWrapper>
                                                    )}
                                                </StyledWrapper>
                                                <Link
                                                    onClick={() => {
                                                        removeCartLineClickHandler &&
                                                            removeCartLineClickHandler(cartLine);
                                                    }}
                                                    data-test-selector="cartline_removeLine"
                                                    {...styles.removeCartLineLink}
                                                />
                                            </StyledWrapper>
                                        </CartLineContext.Provider>
                                    );
                                })}
                            </StyledWrapper>
                        </FancyScroll>
                        <StyledWrapper {...styles.actionWrapper}>
                            <StyledWrapper {...styles.subTotalWrapper}>
                                <Typography {...styles.subTotalTitle}>{translate("SubTotal")}:</Typography>
                                <Typography {...styles.subTotalValue}>{orderSubTotalDisplay}</Typography>
                            </StyledWrapper>

                            {!canSubmitRequisition ? (
                                <Button
                                    {...styles.checkoutButton}
                                    onClick={checkoutHandler}
                                    disabled={!!isCheckoutButtonDisabled}
                                >
                                    {cart?.requiresApproval && !canSubmitRequisition
                                        ? translate("Checkout for Approval")
                                        : isPunchOutOrder
                                        ? BLUEPRINT_NAME.toLowerCase() === "hardwoodsus" ? siteMessage("HWUS_Cart_SubmitPunchOutOrder") : siteMessage("HW_Cart_SubmitPunchOutOrder")
                                        : translate("Proceed to Checkout")}
                                </Button>
                            ) : (
                                <Button
                                    {...styles.checkoutButton}
                                    onClick={submitRequisitionClickHandler}
                                    data-test-selector="cartTotal_SubmitRequisition"
                                >
                                    {translate("Submit Requisition")}
                                </Button>
                            )}
                        </StyledWrapper>
                    </>
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniCart);
